/**
 * Note this is not a singleton. An instance is created each time the session picker modal is opened
 * so it can cache the existing sessions in the cart/order.
 *
 * @constructor
 *
 * @param {Cart} [cart]
 * @param {Order} [existingOrder]
 */
const SessionSpaceCalculator = function (
    cart,
    existingOrder
) {
    /**
     * Sometimes we want to manually adjust the number of spaces displayed for a session.
     * e.g. when we are bulk moving lines to a new session we want to display the availability of every session,
     * without deducting those lines that are being moved (normally they would be deducted because they are already
     * in the cart).
     *
     * To do this set this property to an object with a positive or negative number for each sessionID.
     *
     * @type {Object<number>}
     */
    this.sessionSpaceAdjustments = {};

    /**
     * A cache of the session spaces in the current cart.
     *
     * @type {Object<number, number>}
     */
    this.sessionSpacesInCart = cart ? this.getSessionSpacesInCart(cart) : {};

    /**
     * A cache of the session spaces in the current cart.
     *
     * @type {Object<number, number>}
     */
    this.sessionSpacesInOrder = existingOrder ? this.getSessionSpacesInOrder(existingOrder) : {};
};

SessionSpaceCalculator.prototype = {
    /**
     * @private
     *
     * @param {Cart} cart
     *
     * @return {Object<number, number>}
     */
    getSessionSpacesInCart(cart) {
        let sessions = {};

        cart.itemList.forEach((cartItem) => {
            if (cartItem.session) {
                if (!sessions.hasOwnProperty(cartItem.session.ID)) {
                    sessions[cartItem.session.ID] = 0;
                }

                let linePeople = cartItem.item.people ? parseInt(cartItem.item.people, 10) : 0;
                sessions[cartItem.session.ID] += (cartItem.getQuantity() * linePeople);
            }
        });

        return sessions;
    },

    /**
     * @private
     *
     * @param {Order} order
     *
     * @return {Object<number, number>}
     */
    getSessionSpacesInOrder(order) {
        let sessions = {};

        order.items.forEach((orderLine) => {
            if (orderLine.session) {
                if (!sessions.hasOwnProperty(orderLine.session.ID)) {
                    sessions[orderLine.session.ID] = 0;
                }

                let linePeople = orderLine.item.people ? parseInt(orderLine.item.people, 10) : 0;
                sessions[orderLine.session.ID] += (orderLine.getQuantity() * linePeople);
            }
        });

        return sessions;
    },

    /**
     * Returns the number of spaces remaining on a session.
     * Starts with the session.spacesAvailable property.
     *
     * For new orders:
     *     If adding tickets:
     *         takes away spaces already in cart
     *     If moving tickets to new session:
     *         takes away spaces already in cart except on those lines being moved  (TODO)
     *
     * For existing orders being edited:
     *     If adding tickets:
     *         adds spaces already in order minus spaces already in cart
     *     If moving tickets to a new session:
     *         adds spaces already in order minus spaces already in cart except on those lines being moved  (TODO)
     *
     * @param {Session} session
     */
    getBookableSpacesForSession(session) {
        // Start with the number of spaces we know are available.
        let bookableSpaces = session.spacesAvailable;

        // Add back on any spaces that are already booked by the order being edited. These are ours so we can
        // consider them available (to us).
        if (this.sessionSpacesInOrder && this.sessionSpacesInOrder.hasOwnProperty(session.ID)) {
            bookableSpaces += this.sessionSpacesInOrder[session.ID];
        }

        // Take off any spaces used up by other lines for the desired session already in the cart.
        if (this.sessionSpacesInCart && this.sessionSpacesInCart.hasOwnProperty(session.ID)) {
            bookableSpaces -= this.sessionSpacesInCart[session.ID];
        }

        if (this.sessionSpaceAdjustments && this.sessionSpaceAdjustments.hasOwnProperty(session.ID)) {
            bookableSpaces += this.sessionSpaceAdjustments[session.ID];
        }

        return Math.max(0, bookableSpaces);
    },

    /**
     * @param {?Object<number>} adjustments
     */
    setSessionSpaceAdjustments(adjustments) {
        this.sessionSpaceAdjustments = adjustments || {};
    },

    /**
     * Modify the given SessionAvailability object with the appropriate spacesInCart and spacesInOrder counts.
     * Also sets the spacesBookable property.
     *
     * @param {SessionAvailability} availability
     */
    applySpacesInCartToAvailability(availability) {
        availability.spacesInCart = this.sessionSpacesInCart[availability.sessionID] || null;
        availability.spacesInOrder = this.sessionSpacesInOrder[availability.sessionID] || null;

        availability.spacesBookable = availability.spacesAvailable
            - availability.spacesInCart
            + availability.spacesInOrder;
    },
};

module.exports = SessionSpaceCalculator;
